import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowUpDown,
  ClipboardList,
  Download,
  Filter,
  GitFork,
  LayoutGrid,
  Lock,
  MoreHorizontal,
  Package,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Sparkles,
  Star,
  Table2,
  Upload,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Navigate } from "react-router";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { apiError, showApiError, showApiErrorAs } from "@/lib/api-error";
import { formatCurrency, formatNumber } from "@/lib/format";
import { useAdminOrgStore } from "@/stores/admin-org";
import { useResolvedConfig } from "@/hooks/use-resolved-config";
import { useClaims } from "@/hooks/use-claims";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SmartImportWizard } from "@/components/smart-import-wizard";
import { PreprocessingProgress } from "@/components/preprocessing-progress";
import { PaginationBar } from "@/components/pagination-bar";
import {
  ItemForm,
  itemFormToBody,
  type FoodCategory,
  type StorageType,
  type ItemFormData,
} from "@/components/inventory-item-form";
import { itemDisplayName } from "@/lib/inventory";
import { allergenLabel, EU_14_ALLERGENS } from "@/lib/allergens";
import { storageTypeLabel } from "@/lib/storage-types";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

// ── Types ──────────────────────────────────────────────────────────────

interface AvailableCatalogue {
  id: string;
  name: string;
  type: string;
  item_count: number;
  country_code: string | null;
  source: string;
  is_active: boolean;
  is_readonly: boolean;
}

interface InventoryItem {
  id: string;
  list_id: string;
  description: string;
  clean_name: string | null;
  simple_name: string | null;
  sku: string | null;
  category_id: string | null;
  subcategory_id: string | null;
  storage_type_id: string | null;
  unit_pack_size: string | null;
  price: number | null;
  currency: string;
  price_per_kg: number | null;
  typical_shelf_life_days: number | null;
  typical_weight_kg: number | null;
  edible_yield_pct: number | null;
  allergens: string[];
  aliases: string[];
  supplier: string | null;
  status: string;
  storage_types: { name: string; display_name: string } | null;
  recipe_count: number;
  category: { name: string } | null;
  subcategory: { name: string } | null;
  sort_order: number;
}

// ── Helpers ────────────────────────────────────────────────────────────

function SortHeader({
  label,
  field,
  current,
  dir,
  onToggle,
  align,
}: {
  label: string;
  field: string;
  current: string;
  dir: "asc" | "desc";
  onToggle: (f: string) => void;
  align?: "right";
}) {
  const active = current === field;
  return (
    <span
      role="button"
      tabIndex={0}
      className={`inline-flex cursor-pointer select-none items-center gap-1 ${align === "right" ? "justify-end" : ""}`}
      onClick={() => onToggle(field)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onToggle(field);
      }}
    >
      {label}
      <ArrowUpDown
        className={`size-3 ${active ? "text-gray-900" : "text-gray-300"} ${active && dir === "desc" ? "rotate-180" : ""}`}
      />
    </span>
  );
}

function PillSelector({
  options,
  selected,
  onToggle,
}: {
  options: { id: string; label: string }[];
  selected: string[];
  onToggle: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          className={`rounded-full border px-2 py-0.5 text-xs transition-colors ${selected.includes(o.id) ? "border-gray-900 bg-gray-900 text-white" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}
          onClick={() => onToggle(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function SearchSelect({
  options,
  value,
  onChange,
  placeholder,
}: {
  options: { id: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const selectedLabel = options.find((o) => o.id === value)?.label;
  const filtered = useMemo(() => {
    if (!query) return options.slice(0, 8);
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 8);
  }, [options, query]);

  if (selectedLabel) {
    return (
      <div className="flex items-center gap-1.5">
        <Badge variant="secondary" className="gap-1 rounded-full px-2 py-0.5 text-xs">
          {selectedLabel}
          <button type="button" onClick={() => onChange("")}>
            <X className="size-3" />
          </button>
        </Badge>
      </div>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Input
          readOnly
          value=""
          placeholder={placeholder}
          className="h-8 cursor-pointer text-xs"
          onClick={() => setOpen(true)}
        />
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        <Input
          placeholder={t("common.searchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mb-1 h-7 text-xs"
        />
        {filtered.map((o) => (
          <button
            key={o.id}
            type="button"
            className="w-full rounded px-2 py-1 text-left text-xs hover:bg-gray-100"
            onClick={() => {
              onChange(o.id);
              setOpen(false);
              setQuery("");
            }}
          >
            {o.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

// ── Page ────────────────────────────────────────────────────────────────

export default function CataloguePage() {
  // Switching the org catalogue is a platform-admin-only operation (POS2-789).
  // Org admins get a read-only "Active catalogue" field on Settings → Organisation.
  const { isPlatformAdmin } = useClaims();
  if (!isPlatformAdmin) {
    return <Navigate to="/settings/organisation" replace />;
  }
  return <CataloguePageInner />;
}

function CataloguePageInner() {
  const { locale, t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  // Fetch available catalogues
  const cataloguesQuery = useQuery<{ catalogues: AvailableCatalogue[]; active: string }>({
    queryKey: ["catalogue-available"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory/available-catalogues`, { headers });
      if (!res.ok) throw new Error("Failed to load catalogues");
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const catalogues = cataloguesQuery.data?.catalogues ?? [];
  const activeCatalogue = catalogues.find((c) => c.is_active);
  const defaultCatalogues = catalogues.filter(
    (c) => c.source === "master_default" || c.source === "country_default",
  );
  const orgCatalogue = catalogues.find((c) => c.source === "org_default");
  const isOrgOwned = !!orgCatalogue;

  // Selected catalogue — defaults to active, user can browse others without copying
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showSetDefault, setShowSetDefault] = useState(false);
  const selectedCatalogue = selectedId
    ? catalogues.find((c) => c.id === selectedId)
    : activeCatalogue;
  const listId = selectedCatalogue?.id;
  const isSelectedReadonly = selectedCatalogue?.is_readonly ?? true;

  function invalidateAll() {
    queryClient.invalidateQueries({ queryKey: ["catalogue-available"] });
    queryClient.invalidateQueries({ queryKey: ["catalogue-items"] });
  }

  // Fork mutation
  const forkMutation = useMutation({
    mutationFn: async () => {
      const code =
        String.fromCharCode(65 + Math.floor(Math.random() * 26)) +
        String.fromCharCode(65 + Math.floor(Math.random() * 26));
      const res = await fetch(`${API_URL}/api/v1/inventory/fork-org`, {
        method: "POST",
        headers,
        body: JSON.stringify({ list_code: code }),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return res.json();
    },
    onSuccess: () => {
      invalidateAll();
      toast.success(t("settings.catalogueCreatedFromMaster"));
    },
    onError: showApiError,
  });

  // Create empty list mutation (for upload path)
  const createEmptyMutation = useMutation({
    mutationFn: async () => {
      const code =
        String.fromCharCode(65 + Math.floor(Math.random() * 26)) +
        String.fromCharCode(65 + Math.floor(Math.random() * 26));
      const res = await fetch(`${API_URL}/api/v1/inventory/create-empty-org`, {
        method: "POST",
        headers,
        body: JSON.stringify({ name: "Organisation Catalogue", list_code: code }),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return (await res.json()).data as { id: string };
    },
    onSuccess: () => invalidateAll(),
    onError: showApiError,
  });

  // Reset to master
  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory/reset-org-default`, {
        method: "POST",
        headers,
      });
      if (!res.ok) throw new Error("Failed to reset");
      return (await res.json()).data as { catalogue_name: string };
    },
    onSuccess: (data) => {
      invalidateAll();
      toast.success(t("settings.revertedTo", { name: data.catalogue_name }));
    },
    onError: showApiError,
  });

  // Set as default
  const isSelectedDefault = selectedCatalogue?.id === activeCatalogue?.id;
  const setDefaultMutation = useMutation({
    mutationFn: async (listId: string) => {
      const res = await fetch(`${API_URL}/api/v1/inventory/assign-org-default`, {
        method: "POST",
        headers,
        body: JSON.stringify({ list_id: listId }),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return res.json();
    },
    onSuccess: () => {
      invalidateAll();
      toast.success(t("settings.defaultCatalogueUpdated"));
      // POS2-731: the resolved catalogue changed — existing menus stay matched
      // to the old one until re-processed. Nudge the user to re-process.
      toast.info(t("settings.reprocessMenusHint"));
    },
    onError: showApiError,
  });

  if (cataloguesQuery.isLoading) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (cataloguesQuery.isError || !activeCatalogue) {
    return (
      <div className="p-6">
        <div className="rounded border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-700">{t("settings.catalogueLoadFailed")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 flex flex-col gap-4 bg-gray-50/80 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-lg font-medium text-gray-900">{t("settings.catalogueTitle")}</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            {isSelectedReadonly && <Lock className="mr-1 inline size-3" />}
            {t("settings.catalogueViewing")}{" "}
            <span className="font-medium text-gray-700">
              {selectedCatalogue?.name ?? t("settings.masterCatalogue")}
            </span>
            {isSelectedReadonly
              ? t("settings.readOnlySuffix")
              : t("settings.itemCountSuffix", {
                  count: formatNumber(locale, selectedCatalogue?.item_count ?? 0),
                })}
          </p>
        </div>
        {/* Catalogue actions */}
        <div className="flex items-center gap-2">
          {!isSelectedDefault && selectedCatalogue && (
            <Dialog open={showSetDefault} onOpenChange={setShowSetDefault}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Star className="mr-1.5 size-4" />
                  {t("settings.setAsDefault")}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t("settings.setDefaultTitle")}</DialogTitle>
                  <DialogDescription>
                    {t("settings.setDefaultBodyPrefix")}{" "}
                    <span className="font-medium text-gray-900">{selectedCatalogue.name}</span>{" "}
                    {t("settings.setDefaultBodySuffix")}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSetDefault(false)}>
                    {t("common.cancel")}
                  </Button>
                  <Button
                    onClick={() => {
                      setDefaultMutation.mutate(selectedCatalogue.id);
                      setShowSetDefault(false);
                    }}
                    disabled={setDefaultMutation.isPending}
                  >
                    {setDefaultMutation.isPending
                      ? t("settings.setting")
                      : t("settings.setAsDefault")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Package className="mr-1.5 size-4" />
                {t("settings.switchCatalogue")}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              {defaultCatalogues.map((cat) => (
                <DropdownMenuItem
                  key={cat.id}
                  onClick={() => setSelectedId(cat.id)}
                  disabled={cat.id === selectedCatalogue?.id}
                >
                  <ClipboardList className="mr-2 size-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">{cat.name}</p>
                    <p className="text-xs text-gray-400">
                      {t("settings.itemsCountryReadonly", {
                        count: formatNumber(locale, cat.item_count),
                        source:
                          cat.source === "country_default"
                            ? (cat.country_code ?? t("settings.sourceCountry"))
                            : t("settings.sourceMaster"),
                      })}
                    </p>
                  </div>
                  {cat.id === activeCatalogue?.id && (
                    <Badge className="ml-2 shrink-0 bg-blue-50 text-blue-700 border-0 text-[10px]">
                      {t("settings.badgeDefault")}
                    </Badge>
                  )}
                  {cat.id === selectedCatalogue?.id && cat.id !== activeCatalogue?.id && (
                    <Badge className="ml-2 shrink-0 bg-emerald-50 text-emerald-700 border-0 text-[10px]">
                      {t("settings.badgeViewing")}
                    </Badge>
                  )}
                </DropdownMenuItem>
              ))}
              {orgCatalogue && (
                <DropdownMenuItem
                  onClick={() => setSelectedId(orgCatalogue.id)}
                  disabled={orgCatalogue.id === selectedCatalogue?.id}
                >
                  <Package className="mr-2 size-4 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">{orgCatalogue.name}</p>
                    <p className="text-xs text-gray-400">
                      {t("settings.itemsCount", {
                        count: formatNumber(locale, orgCatalogue.item_count),
                      })}
                    </p>
                  </div>
                  {orgCatalogue.id === activeCatalogue?.id && (
                    <Badge className="ml-2 shrink-0 bg-blue-50 text-blue-700 border-0 text-[10px]">
                      {t("settings.badgeDefault")}
                    </Badge>
                  )}
                  {orgCatalogue.id === selectedCatalogue?.id &&
                    orgCatalogue.id !== activeCatalogue?.id && (
                      <Badge className="ml-2 shrink-0 bg-emerald-50 text-emerald-700 border-0 text-[10px]">
                        {t("settings.badgeViewing")}
                      </Badge>
                    )}
                </DropdownMenuItem>
              )}
              {!isOrgOwned && (
                <>
                  <DropdownMenuItem
                    onClick={() => forkMutation.mutate()}
                    disabled={forkMutation.isPending}
                  >
                    <GitFork className="mr-2 size-4" />
                    {t("settings.forkCurrentList")}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => createEmptyMutation.mutate()}
                    disabled={createEmptyMutation.isPending}
                  >
                    <Upload className="mr-2 size-4" />
                    {t("settings.uploadOwnCatalogue")}
                  </DropdownMenuItem>
                </>
              )}
              {isOrgOwned && (
                <DropdownMenuItem
                  onClick={() => resetMutation.mutate()}
                  disabled={resetMutation.isPending}
                >
                  <RotateCcw className="mr-2 size-4" />
                  {t("settings.revertToDefault")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {!isOrgOwned && (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
          <ClipboardList className="size-8 text-gray-300" />
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{t("settings.createOrgCatalogue")}</p>
            <p className="mt-0.5 text-xs text-gray-500">{t("settings.createOrgCatalogueHint")}</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => forkMutation.mutate()}
              disabled={forkMutation.isPending}
            >
              <GitFork className="mr-1.5 size-4" />
              {forkMutation.isPending ? t("settings.forking") : t("settings.forkMasterList")}
            </Button>
            <Button
              size="sm"
              onClick={() => createEmptyMutation.mutate()}
              disabled={createEmptyMutation.isPending}
            >
              <Upload className="mr-1.5 size-4" />
              {createEmptyMutation.isPending
                ? t("settings.creating")
                : t("settings.uploadOwnCatalogue")}
            </Button>
          </div>
        </div>
      )}

      {listId && <CatalogueItems listId={listId} isReadonly={isSelectedReadonly} />}
    </div>
  );
}

// ── Item list ──────────────────────────────────────────────────────────

function CatalogueItems({ listId, isReadonly }: { listId: string; isReadonly: boolean }) {
  const { locale, t } = useTranslation();
  // Item prices follow the org→site→kitchen config stack — the catalogue is org-scoped, so this
  // resolves at the org. The `€` these figures used to hardcode was wrong for every non-euro
  // organisation (POS2-1134 CP5b).
  const { orgId: claimsOrgId, isPlatformAdmin } = useClaims();
  const adminOrgId = useAdminOrgStore((s) => s.orgId);
  const orgId = isPlatformAdmin ? adminOrgId : claimsOrgId;
  const resolvedCurrency = useResolvedConfig("organisation", orgId || null).data?.config.currency
    ?.value;
  const currency = typeof resolvedCurrency === "string" ? resolvedCurrency : "GBP";
  const headers = useApiHeaders();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<InventoryItem | null>(null);
  const [processingDialogOpen, setProcessingDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [view, setView] = useState<"table" | "cards">("table");
  const [sortBy, setSortBy] = useState("sort_order");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [importOpen, setImportOpen] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  // Processing status monitoring
  const processingQuery = useQuery<{ status: string; total: number }>({
    queryKey: ["preprocessing-status", listId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/preprocess/status`, {
        headers,
      });
      if (!res.ok) throw new Error("Failed to fetch status");
      return (await res.json()).data;
    },
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "processing" ? 3000 : false;
    },
    enabled: !!headers.Authorization.replace("Bearer ", "") && !isReadonly,
  });
  const listProcessing = processingQuery.data?.status === "processing";

  async function handlePreprocess() {
    try {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/preprocess`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        if (res.status === 409) {
          toast.info(t("settings.processingAlreadyRunning"));
          return;
        }
        throw await apiError(t, res);
      }
      processingQuery.refetch();
      setProcessingDialogOpen(true);
    } catch (err) {
      showApiErrorAs(t("settings.startProcessingFailed"), err as Error);
    }
  }

  async function handleReprocess() {
    if (
      !confirm(
        t("settings.reprocessConfirm", {
          count: formatNumber(locale, processingQuery.data?.total ?? totalCount),
        }),
      )
    )
      return;

    try {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/reprocess`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        if (res.status === 409) {
          toast.info(t("settings.processingAlreadyRunning"));
          return;
        }
        throw await apiError(t, res);
      }
      processingQuery.refetch();
      setProcessingDialogOpen(true);
    } catch (err) {
      showApiErrorAs(t("settings.startReprocessingFailed"), err as Error);
    }
  }

  // Filters
  const [filterStorageTypes, setFilterStorageTypes] = useState<string[]>([]);
  const [filterAllergens, setFilterAllergens] = useState<string[]>([]);
  const [filterCategoryId, setFilterCategoryId] = useState("");
  const [filterSubcategoryId, setFilterSubcategoryId] = useState("");
  const [shelfLifeRange, setShelfLifeRange] = useState<[number, number]>([0, 30]);
  const shelfLifeActive = shelfLifeRange[0] > 0 || shelfLifeRange[1] < 30;

  const activeFilterCount = [
    filterStorageTypes.length > 0,
    filterAllergens.length > 0,
    !!filterCategoryId,
    shelfLifeActive,
  ].filter(Boolean).length;

  function clearFilters() {
    setFilterStorageTypes([]);
    setFilterAllergens([]);
    setFilterCategoryId("");
    setFilterSubcategoryId("");
    setShelfLifeRange([0, 30]);
    setPage(0);
  }

  function toggleSort(field: string) {
    if (sortBy === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(field);
      setSortDir("asc");
    }
    setPage(0);
  }

  const itemsQuery = useQuery<InventoryItem[]>({
    queryKey: [
      "catalogue-items",
      listId,
      search,
      page,
      sortBy,
      sortDir,
      pageSize,
      filterStorageTypes,
      filterAllergens,
      filterCategoryId,
      filterSubcategoryId,
      shelfLifeRange,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(page * pageSize),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      if (search) params.set("search", search);
      for (const id of filterStorageTypes) params.append("storage_type_id", id);
      for (const a of filterAllergens) params.append("allergen", a);
      if (filterCategoryId) params.append("category_id", filterCategoryId);
      if (filterSubcategoryId) params.append("subcategory_id", filterSubcategoryId);
      if (shelfLifeRange[0] > 0) params.set("shelf_life_min", String(shelfLifeRange[0]));
      if (shelfLifeRange[1] < 30) params.set("shelf_life_max", String(shelfLifeRange[1]));
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/items?${params}`, {
        headers,
      });
      if (!res.ok) throw new Error("Failed to load items");
      const count = res.headers.get("X-Total-Count");
      if (count) setTotalCount(Number(count));
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
    refetchInterval: listProcessing ? 5000 : false,
  });

  const categoriesQuery = useQuery<FoodCategory[]>({
    queryKey: ["food-categories"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/food-categories`, { headers });
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const storageTypesQuery = useQuery<StorageType[]>({
    queryKey: ["storage-types"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/storage-types`, { headers });
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const topCategories = useMemo(
    () => (categoriesQuery.data ?? []).filter((c) => !c.parent_id),
    [categoriesQuery.data],
  );
  function subcategoriesFor(parentId: string) {
    return (categoriesQuery.data ?? []).filter((c) => c.parent_id === parentId);
  }

  function invalidateItems() {
    queryClient.invalidateQueries({ queryKey: ["catalogue-items", listId] });
    queryClient.invalidateQueries({ queryKey: ["catalogue-resolve"] });
  }

  async function handleExport() {
    try {
      const h: Record<string, string> = {};
      for (const [k, v] of Object.entries(headers)) {
        if (k !== "Content-Type") h[k] = v;
      }
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/export`, { headers: h });
      if (!res.ok) throw await apiError(t, res);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "catalogue-export.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showApiErrorAs(t("common.exportFailed"), err as Error);
    }
  }

  const createItemMutation = useMutation({
    mutationFn: async ({
      data,
      allergens,
      aliases,
    }: {
      data: ItemFormData;
      allergens: string[];
      aliases: string[];
    }) => {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/items`, {
        method: "POST",
        headers,
        body: JSON.stringify(itemFormToBody(data, allergens, aliases)),
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
      return res.json();
    },
    onSuccess: () => {
      invalidateItems();
      toast.success(t("settings.itemAdded"));
      setCreateOpen(false);
    },
    onError: showApiError,
  });

  const updateItemMutation = useMutation({
    mutationFn: async ({
      itemId,
      data,
      allergens,
      aliases,
      cleanName,
      simpleName,
    }: {
      itemId: string;
      data: ItemFormData;
      allergens: string[];
      aliases: string[];
      cleanName?: string;
      simpleName?: string;
    }) => {
      const res = await fetch(`${API_URL}/api/v1/inventory-items/${itemId}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(itemFormToBody(data, allergens, aliases, cleanName, simpleName)),
      });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    onSuccess: () => {
      invalidateItems();
      toast.success(t("settings.itemUpdated"));
      setEditingItem(null);
    },
    onError: showApiError,
  });

  const deleteItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      const res = await fetch(`${API_URL}/api/v1/inventory-items/${itemId}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw new Error("Failed");
    },
    onSuccess: () => {
      invalidateItems();
      toast.success(t("settings.itemDeleted"));
    },
    onError: showApiError,
  });

  const items = itemsQuery.data ?? [];

  if (importOpen) {
    return (
      <SmartImportWizard
        listId={listId}
        open={importOpen}
        onOpenChange={setImportOpen}
        queryKeysToInvalidate={[["catalogue-items"], ["catalogue-available"]]}
        onImported={() => toast.info(t("settings.reprocessMenusHint"))}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Actions bar */}
      <div className="flex items-center gap-3 pb-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder={t("settings.searchItems")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className="pl-9"
          />
        </div>
        <Popover modal={false}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="icon" className="relative size-8">
              <Filter className="size-4" />
              {activeFilterCount > 0 && (
                <span className="absolute -right-1 -top-1 flex size-4 items-center justify-center rounded-full bg-gray-900 text-[10px] text-white">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 space-y-4 p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-900">{t("settings.filters")}</p>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  className="text-xs text-gray-400 hover:text-gray-600"
                  onClick={clearFilters}
                >
                  {t("settings.clearAll")}
                </button>
              )}
            </div>
            <div>
              <p className="mb-1.5 text-xs text-gray-400">{t("settings.storageType")}</p>
              <PillSelector
                options={(storageTypesQuery.data ?? []).map((st) => ({
                  id: st.id,
                  label: storageTypeLabel(t, st) ?? st.display_name,
                }))}
                selected={filterStorageTypes}
                onToggle={(v) => {
                  setFilterStorageTypes((prev) =>
                    prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
                  );
                  setPage(0);
                }}
              />
            </div>
            <div>
              <p className="mb-1.5 text-xs text-gray-400">{t("settings.allergens")}</p>
              <PillSelector
                options={EU_14_ALLERGENS.map((a) => ({ id: a, label: allergenLabel(t, a) }))}
                selected={filterAllergens}
                onToggle={(v) => {
                  setFilterAllergens((prev) =>
                    prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
                  );
                  setPage(0);
                }}
              />
            </div>
            <div>
              <p className="mb-1 text-xs text-gray-400">{t("settings.category")}</p>
              <SearchSelect
                options={topCategories.map((c) => ({ id: c.id, label: c.name }))}
                value={filterCategoryId}
                onChange={(v) => {
                  setFilterCategoryId(v);
                  setFilterSubcategoryId("");
                  setPage(0);
                }}
                placeholder={t("settings.searchCategories")}
              />
            </div>
            <div>
              <p className="mb-1 text-xs text-gray-400">{t("settings.subcategory")}</p>
              <SearchSelect
                options={(filterCategoryId
                  ? subcategoriesFor(filterCategoryId)
                  : (categoriesQuery.data ?? []).filter((c) => c.parent_id)
                ).map((c) => ({ id: c.id, label: c.name }))}
                value={filterSubcategoryId}
                onChange={(v) => {
                  setFilterSubcategoryId(v);
                  setPage(0);
                }}
                placeholder={t("settings.searchSubcategories")}
              />
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <p className="text-xs text-gray-400">{t("settings.shelfLife")}</p>
                <p className="text-xs tabular-nums text-gray-500">
                  {t("settings.shelfLifeRange", {
                    from: shelfLifeRange[0],
                    to:
                      shelfLifeRange[1] >= 30
                        ? t("settings.shelfLifeMax")
                        : t("settings.shelfLifeDays", { days: shelfLifeRange[1] }),
                  })}
                </p>
              </div>
              <Slider
                min={0}
                max={30}
                step={1}
                value={shelfLifeRange}
                onValueChange={(v) => {
                  setShelfLifeRange(v as [number, number]);
                  setPage(0);
                }}
              />
            </div>
          </PopoverContent>
        </Popover>
        <div className="flex items-center rounded-md border border-gray-200">
          <Button
            size="sm"
            variant={view === "table" ? "secondary" : "ghost"}
            className="h-8 rounded-r-none px-2.5"
            onClick={() => setView("table")}
          >
            <Table2 className="size-4" />
          </Button>
          <Button
            size="sm"
            variant={view === "cards" ? "secondary" : "ghost"}
            className="h-8 rounded-l-none px-2.5"
            onClick={() => setView("cards")}
          >
            <LayoutGrid className="size-4" />
          </Button>
        </div>
        {!isReadonly && (
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="size-8">
                  <MoreHorizontal className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setImportOpen(true)}>
                  <Upload className="mr-2 size-4" />
                  {t("settings.importCsv")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport()}>
                  <Download className="mr-2 size-4" />
                  {t("settings.exportCsv")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handlePreprocess()} disabled={listProcessing}>
                  <Sparkles className="mr-2 size-4" />
                  {listProcessing ? t("settings.processing") : t("settings.processCatalogue")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleReprocess()} disabled={listProcessing}>
                  <RefreshCw className="mr-2 size-4" />
                  {t("settings.reprocessSearchNames")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="mr-1.5 size-4" />
                  {t("settings.addItem")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>{t("settings.addItem")}</DialogTitle>
                </DialogHeader>
                <ItemForm
                  categories={topCategories}
                  subcategoriesFor={subcategoriesFor}
                  storageTypes={storageTypesQuery.data ?? []}
                  onSubmit={(data, allergens, aliases) =>
                    createItemMutation.mutate({ data, allergens, aliases })
                  }
                  isPending={createItemMutation.isPending}
                  submitLabel={t("settings.addItem")}
                  listId={listId}
                  onEditExisting={(item) => {
                    setCreateOpen(false);
                    setEditingItem(item as unknown as InventoryItem);
                  }}
                />
              </DialogContent>
            </Dialog>
          </>
        )}
        {isReadonly && (
          <Button variant="outline" size="sm" onClick={() => handleExport()}>
            <Download className="mr-1.5 size-4" />
            {t("settings.exportCsv")}
          </Button>
        )}
      </div>

      {/* Processing progress — inline above table */}
      {!isReadonly && (processingDialogOpen || listProcessing) && (
        <div className="rounded-lg border border-gray-200 p-4">
          <PreprocessingProgress listId={listId} />
          {!listProcessing && processingDialogOpen && (
            <div className="mt-3 flex justify-end">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setProcessingDialogOpen(false);
                  queryClient.invalidateQueries({ queryKey: ["catalogue-items", listId] });
                }}
              >
                {t("settings.dismiss")}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Edit dialog */}
      {!isReadonly && (
        <Dialog open={!!editingItem} onOpenChange={(v) => !v && setEditingItem(null)}>
          <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("settings.editItem")}</DialogTitle>
              {editingItem?.clean_name && editingItem.clean_name !== editingItem.description && (
                <p className="text-sm text-gray-500">{editingItem.clean_name}</p>
              )}
            </DialogHeader>
            {editingItem && (
              <ItemForm
                categories={topCategories}
                subcategoriesFor={subcategoriesFor}
                storageTypes={storageTypesQuery.data ?? []}
                defaultCleanName={editingItem.clean_name ?? ""}
                defaultSimpleName={editingItem.simple_name ?? ""}
                defaultValues={{
                  description: editingItem.description,
                  sku: editingItem.sku ?? "",
                  category_id: editingItem.category_id ?? "",
                  subcategory_id: editingItem.subcategory_id ?? "",
                  storage_type_id: editingItem.storage_type_id ?? "",
                  unit_pack_size: editingItem.unit_pack_size ?? "",
                  price: editingItem.price ?? undefined,
                  currency: editingItem.currency,
                  price_per_kg: editingItem.price_per_kg ?? undefined,
                  typical_shelf_life_days: editingItem.typical_shelf_life_days ?? undefined,
                  typical_weight_kg: editingItem.typical_weight_kg ?? undefined,
                  edible_yield_pct: editingItem.edible_yield_pct ?? undefined,
                  supplier: editingItem.supplier ?? "",
                  status: editingItem.status as ItemFormData["status"],
                }}
                defaultAllergens={editingItem.allergens}
                defaultAliases={editingItem.aliases}
                onSubmit={(data, allergens, aliases, cleanName, simpleName) =>
                  updateItemMutation.mutate({
                    itemId: editingItem.id,
                    data,
                    allergens,
                    aliases,
                    cleanName,
                    simpleName,
                  })
                }
                isPending={updateItemMutation.isPending}
                submitLabel="Save Changes"
                onDelete={() => {
                  if (confirm(`Delete "${editingItem.description}"?`))
                    deleteItemMutation.mutate(editingItem.id);
                }}
                isDeleting={deleteItemMutation.isPending}
              />
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Content */}
      <div className="min-h-0 flex-1 overflow-auto">
        {itemsQuery.isLoading && (
          <div className="space-y-2">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        )}
        {itemsQuery.isError && (
          <div className="rounded border border-red-200 bg-red-50 p-3">
            <p className="text-sm text-red-700">{t("settings.itemsLoadFailed")}</p>
          </div>
        )}
        {!itemsQuery.isLoading && items.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-12 text-gray-400">
            <Package className="size-8" />
            <p className="text-sm">
              {search || activeFilterCount > 0
                ? t("settings.noItemsMatch")
                : t("settings.noItemsInCatalogue")}
            </p>
          </div>
        )}

        {items.length > 0 && view === "table" && (
          <div className="rounded-lg border border-black/[0.06] bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[35%]">
                    <SortHeader
                      label={t("settings.columnItem")}
                      field="description"
                      current={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                  </TableHead>
                  <TableHead>
                    <SortHeader
                      label={t("settings.columnSupplier")}
                      field="supplier"
                      current={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                    />
                  </TableHead>
                  <TableHead>{t("settings.columnCategory")}</TableHead>
                  <TableHead className="text-right">
                    <SortHeader
                      label={t("settings.columnRecipes")}
                      field="recipe_count"
                      current={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                      align="right"
                    />
                  </TableHead>
                  <TableHead className="text-right">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <SortHeader
                            label={t("settings.columnEstYield")}
                            field="edible_yield_pct"
                            current={sortBy}
                            dir={sortDir}
                            onToggle={toggleSort}
                            align="right"
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{t("settings.estYieldTooltip")}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableHead>
                  <TableHead className="text-right">
                    <SortHeader
                      label={t("settings.columnUnitPrice")}
                      field="price"
                      current={sortBy}
                      dir={sortDir}
                      onToggle={toggleSort}
                      align="right"
                    />
                  </TableHead>
                  {!isReadonly && <TableHead className="w-8" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    className={`group border-black/[0.06] transition-all duration-[120ms] ease-out hover:border-l-2 hover:border-l-blue-500 hover:bg-black/[0.02] [&:first-child]:border-t-0 [&:last-child]:border-b-0 ${!isReadonly ? "cursor-pointer" : ""}`}
                    onClick={() => !isReadonly && setEditingItem(item)}
                  >
                    <TableCell className="py-2.5">
                      <div className="flex items-start gap-2">
                        {item.status !== "active" && (
                          <span
                            className={`mt-1.5 size-2 shrink-0 rounded-full ${item.status === "pending_review" || item.status === "seasonal" ? "bg-orange-500" : "bg-red-500"} ${item.status === "pending_review" && listProcessing ? "animate-pulse" : ""}`}
                            title={
                              item.status === "pending_review" && listProcessing
                                ? t("settings.processing")
                                : item.status
                            }
                          />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold leading-tight text-gray-900">
                            {itemDisplayName(item)}
                            {item.allergens.length > 0 &&
                              item.allergens.map((a) => (
                                <Badge
                                  key={a}
                                  className="ml-1 rounded-full bg-orange-50 px-1.5 py-0 text-[10px] font-medium text-orange-700 hover:bg-orange-50"
                                >
                                  {allergenLabel(t, a)}
                                </Badge>
                              ))}
                          </p>
                          {itemDisplayName(item) !== item.description && (
                            <p className="mt-0.5 text-xs text-gray-400 italic">
                              {item.description}
                            </p>
                          )}
                          <p className="mt-0.5 text-xs text-gray-400">
                            {[item.unit_pack_size, storageTypeLabel(t, item.storage_types)]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5 text-sm text-gray-500">
                      {item.supplier || "—"}
                    </TableCell>
                    <TableCell className="py-2.5 text-xs text-gray-400">
                      {item.subcategory?.name || item.category?.name || "—"}
                    </TableCell>
                    <TableCell className="py-2.5 text-right">
                      <span
                        className={`text-sm font-medium tabular-nums ${item.recipe_count > 0 ? "text-gray-900" : "text-gray-300"}`}
                      >
                        {item.recipe_count ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className="py-2.5 text-right">
                      {item.edible_yield_pct != null ? (
                        <span className="inline-flex items-center gap-1.5 text-xs tabular-nums text-gray-500">
                          <span className="relative h-1.5 w-10 rounded-full bg-gray-100">
                            <span
                              className={`absolute inset-y-0 left-0 rounded-full ${item.edible_yield_pct >= 80 ? "bg-[#639922]" : item.edible_yield_pct >= 65 ? "bg-[#EF9F27]" : "bg-[#E24B4A]"}`}
                              style={{ width: `${item.edible_yield_pct}%` }}
                            />
                          </span>
                          {item.edible_yield_pct}%
                        </span>
                      ) : (
                        <span className="text-sm text-gray-300">—</span>
                      )}
                    </TableCell>
                    <TableCell className="py-2.5 text-right">
                      <p className="text-sm font-semibold tabular-nums text-gray-900">
                        {item.price != null ? formatCurrency(locale, item.price, currency, 2) : "—"}
                      </p>
                      {item.price_per_kg != null && (
                        <p className="text-[11px] tabular-nums text-gray-400">
                          {t("settings.pricePerKg", {
                            price: formatCurrency(locale, item.price_per_kg, currency, 2),
                          })}
                        </p>
                      )}
                    </TableCell>
                    {!isReadonly && (
                      <TableCell className="py-2.5 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-7 text-gray-400 opacity-0 hover:text-gray-600 group-hover:opacity-100"
                            >
                              <MoreHorizontal className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingItem(item);
                              }}
                            >
                              {t("settings.edit")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (
                                  confirm(t("settings.deleteConfirm", { name: item.description }))
                                )
                                  deleteItemMutation.mutate(item.id);
                              }}
                            >
                              {t("settings.delete")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {items.length > 0 && view === "cards" && (
          <>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-4">
              {items.map((item) => (
                <button
                  key={item.id}
                  className="rounded-lg border border-gray-200 bg-white p-4 text-left transition-shadow hover:shadow-md"
                  onClick={() => !isReadonly && setEditingItem(item)}
                >
                  <p className="text-sm font-semibold leading-tight text-gray-900">
                    {item.description}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    {[item.unit_pack_size, storageTypeLabel(t, item.storage_types)]
                      .filter(Boolean)
                      .join(" · ") || "—"}
                  </p>
                  {item.price != null && (
                    <p className="mt-2 text-sm font-semibold tabular-nums text-gray-900">
                      {formatCurrency(locale, item.price, currency, 2)}
                    </p>
                  )}
                  {item.allergens.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {item.allergens.map((a) => (
                        <Badge
                          key={a}
                          className="rounded-full bg-orange-50 px-1.5 py-0 text-[10px] text-orange-700 hover:bg-orange-50"
                        >
                          {allergenLabel(t, a)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Pagination — pinned at bottom */}
      {items.length > 0 && (
        <PaginationBar
          page={page}
          pageSize={pageSize}
          total={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(0);
          }}
        />
      )}
    </div>
  );
}
